<template>
  <section class="system-logs-view" v-loading="loading">
    <div class="container-small-table">
      <!-- Header with title and time range selector -->
      <div class="listings-navbar">
        <el-row :gutter="20">
          <el-col :lg="6">
            <h2 class="page-title">System Logs</h2>
          </el-col>
          <el-col :lg="18">
            <ul class="d-flex flex-wrap justify-content-end list-items-group-15 align-items-center">
              <li>
                <el-radio-group v-model="dateRangeMode" size="small" @change="onDateRangeModeChange">
                  <el-radio-button label="realtime">Real-time</el-radio-button>
                  <el-radio-button label="historical">Historical</el-radio-button>
                </el-radio-group>
              </li>
              <li v-if="dateRangeMode === 'realtime'">
                <el-select v-model="timeRange" @change="onTimeRangeChange" placeholder="Time Range" size="default">
                  <el-option label="Last 15 mins" :value="15" />
                  <el-option label="Last 30 mins" :value="30" />
                  <el-option label="Last 1 hour" :value="60" />
                  <el-option label="Last 3 hours" :value="180" />
                  <el-option label="Last 6 hours" :value="360" />
                  <el-option label="Last 24 hours" :value="1440" />
                </el-select>
              </li>
              <li v-if="dateRangeMode === 'historical'">
                <el-select v-model="historicalRange" @change="onHistoricalRangeChange" placeholder="Date Range" size="default">
                  <el-option label="Last 2 Days" value="2days" />
                  <el-option label="Last 1 Week" value="1week" />
                  <el-option label="Last 15 Days" value="15days" />
                  <el-option label="Last 30 Days" value="30days" />
                </el-select>
              </li>
              <li>
                <el-button type="primary" @click="refreshData" :loading="loading">
                  <el-icon><Refresh /></el-icon>
                  Refresh
                </el-button>
              </li>
            </ul>
          </el-col>
        </el-row>
      </div>

      <!-- Stats Cards -->
      <div class="stats-cards" v-if="stats">
        <el-row :gutter="20">
          <el-col :xs="12" :sm="6" :lg="3">
            <div class="stat-card">
              <div class="stat-value">{{ stats.totalRequests }}</div>
              <div class="stat-label">Total Requests</div>
            </div>
          </el-col>
          <el-col :xs="12" :sm="6" :lg="3">
            <div class="stat-card stat-error">
              <div class="stat-value">{{ stats.errorCount }}</div>
              <div class="stat-label">Errors</div>
            </div>
          </el-col>
          <el-col :xs="12" :sm="6" :lg="3">
            <div class="stat-card stat-third-party">
              <div class="stat-value">{{ stats.thirdPartyCount }}</div>
              <div class="stat-label">3rd Party Calls</div>
            </div>
          </el-col>
          <el-col :xs="12" :sm="6" :lg="3">
            <div class="stat-card stat-performance">
              <div class="stat-value">{{ stats.avgResponseTime }}ms</div>
              <div class="stat-label">Avg Response Time</div>
            </div>
          </el-col>
        </el-row>
      </div>

      <!-- Tabs for different views -->
      <el-tabs v-model="activeTab" @tab-click="onTabChange">
        <!-- All Logs Tab -->
        <el-tab-pane label="All Logs" name="all">
          <div class="filter-section">
            <el-row :gutter="10">
              <el-col :lg="3">
                <el-input 
                  v-model="filters.search" 
                  placeholder="Search all..." 
                  clearable
                  @clear="fetchLogs"
                  @keyup.enter="fetchLogs"
                >
                  <template #prefix>
                    <el-icon><Search /></el-icon>
                  </template>
                </el-input>
              </el-col>
              <el-col :lg="3">
                <el-input 
                  v-model="filters.accessionId" 
                  placeholder="Accession ID" 
                  clearable
                  @clear="fetchLogs"
                  @keyup.enter="fetchLogs"
                />
              </el-col>
              <el-col :lg="2">
                <el-input 
                  v-model="filters.orderId" 
                  placeholder="Order ID" 
                  clearable
                  @clear="fetchLogs"
                  @keyup.enter="fetchLogs"
                />
              </el-col>
              <el-col :lg="2">
                <el-select v-model="filters.type" placeholder="Type" clearable @change="fetchLogs">
                  <el-option label="API" value="API" />
                  <el-option label="Error" value="ERROR" />
                  <el-option label="Third Party" value="THIRD_PARTY" />
                  <el-option label="Event" value="EVENT" />
                  <el-option label="Performance" value="PERFORMANCE" />
                  <el-option label="Fax" value="FAX" />
                  <el-option label="Email" value="EMAIL" />
                </el-select>
              </el-col>
              <el-col :lg="2">
                <el-select v-model="filters.level" placeholder="Level" clearable @change="fetchLogs">
                  <el-option label="Debug" value="DEBUG" />
                  <el-option label="Info" value="INFO" />
                  <el-option label="Warning" value="WARN" />
                  <el-option label="Error" value="ERROR" />
                  <el-option label="Critical" value="CRITICAL" />
                </el-select>
              </el-col>
              <el-col :lg="3">
                <el-input 
                  v-model="filters.path" 
                  placeholder="API Path" 
                  clearable
                  @clear="fetchLogs"
                  @keyup.enter="fetchLogs"
                />
              </el-col>
              <el-col :lg="2">
                <el-input 
                  v-model="filters.statusCode" 
                  placeholder="Status" 
                  clearable
                  @clear="fetchLogs"
                  @keyup.enter="fetchLogs"
                />
              </el-col>
              <el-col :lg="2">
                <el-input 
                  v-model="filters.minDuration" 
                  placeholder="Min ms" 
                  clearable
                  @clear="fetchLogs"
                  @keyup.enter="fetchLogs"
                />
              </el-col>
              <el-col :lg="2">
                <el-button type="primary" @click="fetchLogs">Search</el-button>
              </el-col>
              <el-col :lg="2">
                <el-button @click="clearFilters">Clear</el-button>
              </el-col>
            </el-row>
          </div>

          <!-- Logs Table -->
          <div class="logs-table">
            <el-table 
              :data="paginatedLogs" 
              height="calc(100vh - 450px)"
              @row-click="showLogDetail"
              highlight-current-row
              stripe
            >
              <el-table-column prop="timestamp" label="Time" width="160">
                <template #default="scope">
                  <span class="log-time">{{ formatTime(scope.row.timestamp) }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="type" label="Type" width="110">
                <template #default="scope">
                  <el-tag :type="getTypeTagColor(scope.row.type)" size="small">
                    {{ scope.row.type }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="level" label="Level" width="90">
                <template #default="scope">
                  <el-tag :type="getLevelTagColor(scope.row.level)" size="small">
                    {{ scope.row.level }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="accessionId" label="Accession ID" width="130">
                <template #default="scope">
                  <span class="accession-id" v-if="scope.row.accessionId">{{ scope.row.accessionId }}</span>
                  <span v-else>--</span>
                </template>
              </el-table-column>
              <el-table-column prop="orderId" label="Order ID" width="110">
                <template #default="scope">
                  <span class="order-id" v-if="scope.row.orderId">{{ scope.row.orderId }}</span>
                  <span v-else>--</span>
                </template>
              </el-table-column>
              <el-table-column prop="message" label="Message" min-width="250">
                <template #default="scope">
                  <span class="log-message" :class="{ 'error-message': scope.row.level === 'ERROR' }">
                    {{ truncateMessage(scope.row.message, 80) }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column prop="path" label="Path" width="180">
                <template #default="scope">
                  <span class="api-path" v-if="scope.row.path">{{ truncateMessage(scope.row.path, 30) }}</span>
                  <span v-else>--</span>
                </template>
              </el-table-column>
              <el-table-column prop="duration" label="Time" width="80">
                <template #default="scope">
                  <span v-if="scope.row.duration" :class="getDurationClass(scope.row.duration)">
                    {{ scope.row.duration }}ms
                  </span>
                  <span v-else>--</span>
                </template>
              </el-table-column>
              <el-table-column prop="statusCode" label="Status" width="70">
                <template #default="scope">
                  <span v-if="scope.row.statusCode" :class="getStatusClass(scope.row.statusCode)">
                    {{ scope.row.statusCode }}
                  </span>
                  <span v-else>--</span>
                </template>
              </el-table-column>
              <el-table-column prop="userEmail" label="User" width="140">
                <template #default="scope">
                  {{ truncateMessage(scope.row.userEmail, 20) || '--' }}
                </template>
              </el-table-column>
              <el-table-column label="Response" width="100">
                <template #default="scope">
                  <el-button 
                    v-if="scope.row.responseBody || scope.row.requestBody" 
                    size="small" 
                    @click.stop="showResponseDetail(scope.row)"
                  >
                    View
                  </el-button>
                  <span v-else>--</span>
                </template>
              </el-table-column>
            </el-table>
            <!-- Pagination for All Logs -->
            <div class="pagination-wrapper">
              <el-pagination
                v-model:current-page="pagination.allLogs.page"
                v-model:page-size="pagination.allLogs.pageSize"
                :page-sizes="[25, 50, 100, 200]"
                layout="total, sizes, prev, pager, next, jumper"
                :total="logs.length"
                @size-change="handleAllLogsSizeChange"
                @current-change="handleAllLogsPageChange"
              />
            </div>
          </div>
        </el-tab-pane>

        <!-- Errors Tab -->
        <el-tab-pane label="Errors" name="errors">
          <div class="errors-summary">
            <el-table :data="paginatedErrors" height="calc(100vh - 350px)" stripe>
              <el-table-column prop="message" label="Error Message" min-width="350">
                <template #default="scope">
                  <span class="error-message-text">{{ truncateMessage(scope.row.message, 100) }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="count" label="Count" width="100">
                <template #default="scope">
                  <el-tag type="danger">{{ scope.row.count }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="lastOccurrence" label="Last Occurrence" width="180">
                <template #default="scope">
                  {{ formatTime(scope.row.lastOccurrence) }}
                </template>
              </el-table-column>
              <el-table-column label="Actions" width="120">
                <template #default="scope">
                  <el-button size="small" @click="showErrorSamples(scope.row)">
                    View Samples
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
            <!-- Pagination for Errors -->
            <div class="pagination-wrapper">
              <el-pagination
                v-model:current-page="pagination.errors.page"
                v-model:page-size="pagination.errors.pageSize"
                :page-sizes="[25, 50, 100]"
                layout="total, sizes, prev, pager, next"
                :total="errors.length"
              />
            </div>
          </div>
        </el-tab-pane>

        <!-- Third Party Tab -->
        <el-tab-pane label="Third Party Calls" name="thirdParty">
          <div class="third-party-summary">
            <el-table :data="paginatedThirdParty" height="calc(100vh - 350px)" stripe>
              <el-table-column prop="service" label="Service" width="180">
                <template #default="scope">
                  <span class="service-name">{{ scope.row.service }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="count" label="Total Calls" width="120">
                <template #default="scope">
                  <el-tag>{{ scope.row.count }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="errors" label="Errors" width="100">
                <template #default="scope">
                  <el-tag :type="scope.row.errors > 0 ? 'danger' : 'success'">
                    {{ scope.row.errors }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="avgDuration" label="Avg Duration" width="130">
                <template #default="scope">
                  <span :class="getDurationClass(scope.row.avgDuration)">
                    {{ scope.row.avgDuration }}ms
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="Error Rate" width="120">
                <template #default="scope">
                  <span :class="{ 'error-rate-high': getErrorRate(scope.row) > 10 }">
                    {{ getErrorRate(scope.row).toFixed(1) }}%
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="Actions" width="120">
                <template #default="scope">
                  <el-button size="small" @click="showServiceCalls(scope.row)">
                    View Calls
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
            <!-- Pagination for Third Party -->
            <div class="pagination-wrapper">
              <el-pagination
                v-model:current-page="pagination.thirdParty.page"
                v-model:page-size="pagination.thirdParty.pageSize"
                :page-sizes="[25, 50, 100]"
                layout="total, sizes, prev, pager, next"
                :total="thirdParty.length"
              />
            </div>
          </div>
        </el-tab-pane>

        <!-- Performance Tab -->
        <el-tab-pane label="Performance" name="performance">
          <div class="performance-summary">
            <el-alert 
              v-if="performance.length > 0"
              title="Slow Endpoints"
              description="These endpoints are taking longer than 2 seconds to respond"
              type="warning"
              show-icon
              :closable="false"
              style="margin-bottom: 15px"
            />
            <el-table :data="paginatedPerformance" height="calc(100vh - 390px)" stripe>
              <el-table-column prop="endpoint" label="Endpoint" min-width="300">
                <template #default="scope">
                  <span class="endpoint-name">{{ scope.row.endpoint }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="count" label="Slow Requests" width="130">
                <template #default="scope">
                  <el-tag type="warning">{{ scope.row.count }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="avgDuration" label="Avg Duration" width="130">
                <template #default="scope">
                  <span class="duration-slow">{{ scope.row.avgDuration }}ms</span>
                </template>
              </el-table-column>
              <el-table-column prop="maxDuration" label="Max Duration" width="130">
                <template #default="scope">
                  <span class="duration-critical">{{ scope.row.maxDuration }}ms</span>
                </template>
              </el-table-column>
              <el-table-column label="Actions" width="120">
                <template #default="scope">
                  <el-button size="small" @click="showSlowSamples(scope.row)">
                    View Samples
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
            <!-- Pagination for Performance -->
            <div class="pagination-wrapper">
              <el-pagination
                v-model:current-page="pagination.performance.page"
                v-model:page-size="pagination.performance.pageSize"
                :page-sizes="[25, 50, 100]"
                layout="total, sizes, prev, pager, next"
                :total="performance.length"
              />
            </div>
          </div>
        </el-tab-pane>

        <!-- Analytics Tab -->
        <el-tab-pane label="Analytics" name="analytics">
          <div class="analytics-view" v-if="stats">
            <!-- All API Endpoints Section -->
            <el-row :gutter="20" style="margin-bottom: 20px;">
              <el-col :lg="24">
                <div class="analytics-card">
                  <div class="analytics-header">
                    <h4>All API Endpoints</h4>
                    <div class="analytics-search">
                      <el-input 
                        v-model="endpointSearch" 
                        placeholder="Search endpoint..." 
                        clearable
                        size="small"
                        style="width: 250px"
                      >
                        <template #prefix>
                          <el-icon><Search /></el-icon>
                        </template>
                      </el-input>
                    </div>
                  </div>
                  <el-table 
                    :data="paginatedAllEndpoints" 
                    stripe 
                    size="small"
                    :default-sort="{ prop: 'count', order: 'descending' }"
                    @sort-change="handleEndpointSort"
                    height="400"
                  >
                    <el-table-column prop="path" label="Endpoint" min-width="300" sortable="custom">
                      <template #default="scope">
                        <span class="endpoint-path">{{ scope.row.path }}</span>
                      </template>
                    </el-table-column>
                    <el-table-column prop="count" label="Total Calls" width="120" sortable="custom">
                      <template #default="scope">
                        <el-tag size="small">{{ scope.row.count }}</el-tag>
                      </template>
                    </el-table-column>
                    <el-table-column prop="avgTime" label="Avg Time" width="110" sortable="custom">
                      <template #default="scope">
                        <span :class="getDurationClass(scope.row.avgTime)">
                          {{ scope.row.avgTime }}ms
                        </span>
                      </template>
                    </el-table-column>
                    <el-table-column prop="minTime" label="Min Time" width="100" sortable="custom">
                      <template #default="scope">
                        <span class="duration-normal">{{ scope.row.minTime }}ms</span>
                      </template>
                    </el-table-column>
                    <el-table-column prop="maxTime" label="Max Time" width="100" sortable="custom">
                      <template #default="scope">
                        <span :class="getDurationClass(scope.row.maxTime)">
                          {{ scope.row.maxTime }}ms
                        </span>
                      </template>
                    </el-table-column>
                    <el-table-column prop="errorCount" label="Errors" width="90" sortable="custom">
                      <template #default="scope">
                        <el-tag :type="scope.row.errorCount > 0 ? 'danger' : 'success'" size="small">
                          {{ scope.row.errorCount }}
                        </el-tag>
                      </template>
                    </el-table-column>
                    <el-table-column label="Error Rate" width="100">
                      <template #default="scope">
                        <span :class="{ 'error-rate-high': getEndpointErrorRate(scope.row) > 10 }">
                          {{ getEndpointErrorRate(scope.row).toFixed(1) }}%
                        </span>
                      </template>
                    </el-table-column>
                  </el-table>
                  <div class="pagination-wrapper">
                    <el-pagination
                      v-model:current-page="pagination.allEndpoints.page"
                      v-model:page-size="pagination.allEndpoints.pageSize"
                      :page-sizes="[25, 50, 100, 200]"
                      layout="total, sizes, prev, pager, next, jumper"
                      :total="filteredAllEndpoints.length"
                    />
                  </div>
                </div>
              </el-col>
            </el-row>

            <el-row :gutter="20">
              <el-col :lg="12">
                <div class="analytics-card">
                  <h4>Top 10 Slowest Endpoints (Avg Response Time)</h4>
                  <el-table :data="stats.slowestEndpoints" size="small" stripe max-height="300">
                    <el-table-column prop="path" label="Endpoint" />
                    <el-table-column prop="avgTime" label="Avg Time" width="100">
                      <template #default="scope">
                        <span :class="getDurationClass(scope.row.avgTime)">
                          {{ scope.row.avgTime }}ms
                        </span>
                      </template>
                    </el-table-column>
                    <el-table-column prop="count" label="Calls" width="80" />
                  </el-table>
                </div>
              </el-col>
              <el-col :lg="12">
                <div class="analytics-card">
                  <h4>Endpoints with Most Errors</h4>
                  <el-table :data="stats.errorsByPath" size="small" stripe max-height="300">
                    <el-table-column prop="path" label="Endpoint" />
                    <el-table-column prop="count" label="Errors" width="100">
                      <template #default="scope">
                        <el-tag type="danger" size="small">{{ scope.row.count }}</el-tag>
                      </template>
                    </el-table-column>
                  </el-table>
                </div>
              </el-col>
            </el-row>
            <el-row :gutter="20" style="margin-top: 20px;">
              <el-col :lg="24">
                <div class="analytics-card">
                  <h4>Third Party Services</h4>
                  <el-table :data="stats.thirdPartyByService" size="small" stripe max-height="300">
                    <el-table-column prop="service" label="Service" />
                    <el-table-column prop="count" label="Calls" width="100" />
                    <el-table-column prop="avgTime" label="Avg Time" width="100">
                      <template #default="scope">
                        {{ scope.row.avgTime }}ms
                      </template>
                    </el-table-column>
                    <el-table-column prop="errors" label="Errors" width="100">
                      <template #default="scope">
                        <el-tag :type="scope.row.errors > 0 ? 'danger' : 'success'" size="small">
                          {{ scope.row.errors }}
                        </el-tag>
                      </template>
                    </el-table-column>
                  </el-table>
                </div>
              </el-col>
            </el-row>
          </div>
        </el-tab-pane>
      </el-tabs>

      <!-- Log Detail Dialog -->
      <el-dialog 
        v-model="showDetailDialog" 
        title="Log Details" 
        width="900px"
        :close-on-click-modal="false"
      >
        <div v-if="selectedLog" class="log-detail">
          <el-descriptions :column="2" border size="small">
            <el-descriptions-item label="Timestamp">
              {{ formatTime(selectedLog.timestamp) }}
            </el-descriptions-item>
            <el-descriptions-item label="Request ID">
              {{ selectedLog.requestId || '--' }}
            </el-descriptions-item>
            <el-descriptions-item label="Type">
              <el-tag :type="getTypeTagColor(selectedLog.type)" size="small">
                {{ selectedLog.type }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="Level">
              <el-tag :type="getLevelTagColor(selectedLog.level)" size="small">
                {{ selectedLog.level }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="Method" v-if="selectedLog.method">
              {{ selectedLog.method }}
            </el-descriptions-item>
            <el-descriptions-item label="Path" v-if="selectedLog.path">
              {{ selectedLog.path }}
            </el-descriptions-item>
            <el-descriptions-item label="Status Code" v-if="selectedLog.statusCode">
              <span :class="getStatusClass(selectedLog.statusCode)">
                {{ selectedLog.statusCode }}
              </span>
            </el-descriptions-item>
            <el-descriptions-item label="Duration" v-if="selectedLog.duration">
              <span :class="getDurationClass(selectedLog.duration)">
                {{ selectedLog.duration }}ms
              </span>
            </el-descriptions-item>
            <el-descriptions-item label="Accession ID">
              <span class="accession-id-detail">{{ selectedLog.accessionId || '--' }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="Order ID">
              <span class="order-id-detail">{{ selectedLog.orderId || '--' }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="Case ID">
              {{ selectedLog.caseId || '--' }}
            </el-descriptions-item>
            <el-descriptions-item label="Patient ID">
              {{ selectedLog.patientId || '--' }}
            </el-descriptions-item>
            <el-descriptions-item label="User">
              {{ selectedLog.userEmail || '--' }}
            </el-descriptions-item>
            <el-descriptions-item label="User ID">
              {{ selectedLog.userId || '--' }}
            </el-descriptions-item>
            <el-descriptions-item label="IP Address">
              {{ selectedLog.ip || '--' }}
            </el-descriptions-item>
            <el-descriptions-item label="Service Name" v-if="selectedLog.serviceName">
              {{ selectedLog.serviceName }}
            </el-descriptions-item>
          </el-descriptions>

          <div class="message-section" style="margin-top: 20px;">
            <h4>Message</h4>
            <pre class="log-message-detail">{{ selectedLog.message }}</pre>
          </div>

          <div v-if="selectedLog.errorMessage" class="error-section" style="margin-top: 20px;">
            <h4>Error Message</h4>
            <pre class="error-message-detail">{{ selectedLog.errorMessage }}</pre>
          </div>

          <div v-if="selectedLog.stackTrace" class="stack-section" style="margin-top: 20px;">
            <h4>Stack Trace</h4>
            <pre class="stack-trace">{{ selectedLog.stackTrace }}</pre>
          </div>

          <div v-if="selectedLog.queryParams" class="body-section" style="margin-top: 20px;">
            <h4>Query Parameters</h4>
            <pre class="json-body">{{ formatJson(selectedLog.queryParams) }}</pre>
          </div>

          <div v-if="selectedLog.requestBody" class="body-section" style="margin-top: 20px;">
            <h4>Request Body</h4>
            <pre class="json-body">{{ formatJson(selectedLog.requestBody) }}</pre>
          </div>

          <div v-if="selectedLog.responseBody" class="body-section" style="margin-top: 20px;">
            <h4>Response Body</h4>
            <pre class="json-body">{{ formatJson(selectedLog.responseBody) }}</pre>
          </div>

          <div v-if="selectedLog.metadata" class="metadata-section" style="margin-top: 20px;">
            <h4>Metadata</h4>
            <pre class="json-body">{{ formatJson(selectedLog.metadata) }}</pre>
          </div>
        </div>
      </el-dialog>

      <!-- Response Detail Dialog -->
      <el-dialog 
        v-model="showResponseDialog" 
        title="Request/Response Details" 
        width="900px"
        :close-on-click-modal="false"
      >
        <div v-if="responseDetailLog" class="response-detail">
          <el-descriptions :column="2" border size="small" style="margin-bottom: 15px;">
            <el-descriptions-item label="Timestamp">{{ formatTime(responseDetailLog.timestamp) }}</el-descriptions-item>
            <el-descriptions-item label="Path">{{ responseDetailLog.path || '--' }}</el-descriptions-item>
            <el-descriptions-item label="Accession ID">{{ responseDetailLog.accessionId || '--' }}</el-descriptions-item>
            <el-descriptions-item label="Order ID">{{ responseDetailLog.orderId || '--' }}</el-descriptions-item>
          </el-descriptions>

          <el-tabs v-model="responseTab">
            <el-tab-pane label="Request Body" name="request">
              <div class="search-json">
                <el-input v-model="jsonSearchRequest" placeholder="Search in request..." clearable size="small" style="margin-bottom: 10px;" />
              </div>
              <pre class="json-body" v-html="highlightSearch(formatJson(responseDetailLog.requestBody), jsonSearchRequest)"></pre>
            </el-tab-pane>
            <el-tab-pane label="Response Body" name="response">
              <div class="search-json">
                <el-input v-model="jsonSearchResponse" placeholder="Search in response..." clearable size="small" style="margin-bottom: 10px;" />
              </div>
              <pre class="json-body" v-html="highlightSearch(formatJson(responseDetailLog.responseBody), jsonSearchResponse)"></pre>
            </el-tab-pane>
            <el-tab-pane label="Query Params" name="query" v-if="responseDetailLog.queryParams">
              <pre class="json-body">{{ formatJson(responseDetailLog.queryParams) }}</pre>
            </el-tab-pane>
          </el-tabs>
        </div>
      </el-dialog>

      <!-- Samples Dialog -->
      <el-dialog 
        v-model="showSamplesDialog" 
        :title="samplesDialogTitle" 
        width="1000px"
        :close-on-click-modal="false"
      >
        <el-table :data="paginatedSamples" stripe max-height="400">
          <el-table-column prop="timestamp" label="Time" width="160">
            <template #default="scope">
              {{ formatTime(scope.row.timestamp) }}
            </template>
          </el-table-column>
          <el-table-column prop="accessionId" label="Accession ID" width="130">
            <template #default="scope">
              {{ scope.row.accessionId || '--' }}
            </template>
          </el-table-column>
          <el-table-column prop="orderId" label="Order ID" width="110">
            <template #default="scope">
              {{ scope.row.orderId || '--' }}
            </template>
          </el-table-column>
          <el-table-column prop="message" label="Message" min-width="250">
            <template #default="scope">
              {{ truncateMessage(scope.row.message, 60) }}
            </template>
          </el-table-column>
          <el-table-column prop="duration" label="Duration" width="100">
            <template #default="scope">
              {{ scope.row.duration ? `${scope.row.duration}ms` : '--' }}
            </template>
          </el-table-column>
          <el-table-column label="Actions" width="100">
            <template #default="scope">
              <el-button size="small" @click="showLogDetail(scope.row)">
                View
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <!-- Pagination for Samples -->
        <div class="pagination-wrapper" style="margin-top: 15px;">
          <el-pagination
            v-model:current-page="pagination.samples.page"
            v-model:page-size="pagination.samples.pageSize"
            :page-sizes="[10, 25, 50]"
            layout="total, sizes, prev, pager, next"
            :total="sampleLogs.length"
          />
        </div>
      </el-dialog>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { Refresh, Search } from '@element-plus/icons-vue';
import moment from 'moment-timezone';

export default {
  name: 'SystemLogs',
  components: {
    Refresh,
    Search
  },
  data() {
    return {
      activeTab: 'all',
      timeRange: 60,
      dateRangeMode: 'realtime', // 'realtime' or 'historical'
      historicalRange: '2days', // '2days', '1week', '15days', '30days'
      showDetailDialog: false,
      showSamplesDialog: false,
      showResponseDialog: false,
      samplesDialogTitle: '',
      sampleLogs: [],
      responseDetailLog: null,
      responseTab: 'response',
      jsonSearchRequest: '',
      jsonSearchResponse: '',
      endpointSearch: '',
      endpointSort: { prop: 'count', order: 'descending' },
      filters: {
        search: '',
        type: '',
        level: '',
        path: '',
        statusCode: '',
        minDuration: '',
        accessionId: '',
        orderId: ''
      },
      pagination: {
        allLogs: { page: 1, pageSize: 50 },
        errors: { page: 1, pageSize: 25 },
        thirdParty: { page: 1, pageSize: 25 },
        performance: { page: 1, pageSize: 25 },
        samples: { page: 1, pageSize: 25 },
        allEndpoints: { page: 1, pageSize: 50 }
      }
    };
  },
  computed: {
    ...mapGetters('systemLogs', [
      'getLogs',
      'getStats',
      'getErrors',
      'getThirdParty',
      'getPerformance',
      'getLoading',
      'getSelectedLog'
    ]),
    logs() {
      return this.getLogs;
    },
    stats() {
      return this.getStats;
    },
    errors() {
      return this.getErrors;
    },
    thirdParty() {
      return this.getThirdParty;
    },
    performance() {
      return this.getPerformance;
    },
    loading() {
      return this.getLoading;
    },
    selectedLog() {
      return this.getSelectedLog;
    },
    // Paginated data
    paginatedLogs() {
      const { page, pageSize } = this.pagination.allLogs;
      const start = (page - 1) * pageSize;
      return this.logs.slice(start, start + pageSize);
    },
    paginatedErrors() {
      const { page, pageSize } = this.pagination.errors;
      const start = (page - 1) * pageSize;
      return this.errors.slice(start, start + pageSize);
    },
    paginatedThirdParty() {
      const { page, pageSize } = this.pagination.thirdParty;
      const start = (page - 1) * pageSize;
      return this.thirdParty.slice(start, start + pageSize);
    },
    paginatedPerformance() {
      const { page, pageSize } = this.pagination.performance;
      const start = (page - 1) * pageSize;
      return this.performance.slice(start, start + pageSize);
    },
    paginatedSamples() {
      const { page, pageSize } = this.pagination.samples;
      const start = (page - 1) * pageSize;
      return this.sampleLogs.slice(start, start + pageSize);
    },
    // All endpoints with filtering and sorting
    filteredAllEndpoints() {
      let endpoints = this.stats?.allEndpoints || [];
      
      // Apply search filter
      if (this.endpointSearch) {
        const search = this.endpointSearch.toLowerCase();
        endpoints = endpoints.filter(e => e.path.toLowerCase().includes(search));
      }
      
      // Apply sorting
      if (this.endpointSort.prop) {
        endpoints = [...endpoints].sort((a, b) => {
          const valA = a[this.endpointSort.prop];
          const valB = b[this.endpointSort.prop];
          if (typeof valA === 'string') {
            return this.endpointSort.order === 'ascending' 
              ? valA.localeCompare(valB) 
              : valB.localeCompare(valA);
          }
          return this.endpointSort.order === 'ascending' ? valA - valB : valB - valA;
        });
      }
      
      return endpoints;
    },
    paginatedAllEndpoints() {
      const { page, pageSize } = this.pagination.allEndpoints;
      const start = (page - 1) * pageSize;
      return this.filteredAllEndpoints.slice(start, start + pageSize);
    }
  },
  watch: {
    selectedLog(val) {
      this.showDetailDialog = !!val;
    },
    showDetailDialog(val) {
      if (!val) {
        this.clearSelectedLog();
      }
    }
  },
  mounted() {
    this.initializeData();
  },
  beforeUnmount() {
    this.resetState();
  },
  methods: {
    ...mapActions('systemLogs', [
      'fetchLogs',
      'fetchLogsByDateRange',
      'fetchStats',
      'fetchErrors',
      'fetchThirdParty',
      'fetchPerformance',
      'updateFilters',
      'selectLog',
      'clearSelectedLog',
      'resetState'
    ]),

    async initializeData() {
      this.updateFilters({ timeRangeMinutes: this.timeRange });
      await Promise.all([
        this.fetchLogs(),
        this.fetchStats()
      ]);
    },

    async refreshData() {
      // Reset pagination
      this.pagination.allLogs.page = 1;
      this.pagination.errors.page = 1;
      this.pagination.thirdParty.page = 1;
      this.pagination.performance.page = 1;
      
      // Handle historical mode refresh
      if (this.dateRangeMode === 'historical') {
        this.onHistoricalRangeChange();
        return;
      }
      
      // Real-time mode
      switch (this.activeTab) {
        case 'all':
          await this.fetchLogs();
          await this.fetchStats();
          break;
        case 'errors':
          await this.fetchErrors();
          break;
        case 'thirdParty':
          await this.fetchThirdParty();
          break;
        case 'performance':
          await this.fetchPerformance();
          break;
        case 'analytics':
          await this.fetchStats();
          break;
      }
    },

    async onTimeRangeChange() {
      this.updateFilters({ timeRangeMinutes: this.timeRange });
      await this.refreshData();
    },

    onDateRangeModeChange() {
      this.pagination.allLogs.page = 1;
      if (this.dateRangeMode === 'realtime') {
        this.updateFilters({ 
          timeRangeMinutes: this.timeRange,
          startDate: null,
          endDate: null
        });
        this.refreshData();
      } else {
        this.onHistoricalRangeChange();
      }
    },

    onHistoricalRangeChange() {
      const today = moment().format('YYYY-MM-DD');
      let startDate;
      
      switch (this.historicalRange) {
        case '2days':
          startDate = moment().subtract(2, 'days').format('YYYY-MM-DD');
          break;
        case '1week':
          startDate = moment().subtract(7, 'days').format('YYYY-MM-DD');
          break;
        case '15days':
          startDate = moment().subtract(15, 'days').format('YYYY-MM-DD');
          break;
        case '30days':
          startDate = moment().subtract(30, 'days').format('YYYY-MM-DD');
          break;
        default:
          startDate = moment().subtract(2, 'days').format('YYYY-MM-DD');
      }

      this.pagination.allLogs.page = 1;
      this.fetchLogsFromDateRange(startDate, today);
    },

    async fetchLogsFromDateRange(startDate, endDate) {
      await this.fetchLogsByDateRange({
        startDate,
        endDate,
        type: this.filters.type || undefined,
        level: this.filters.level || undefined,
        search: this.filters.search || undefined,
        accessionId: this.filters.accessionId || undefined,
        orderId: this.filters.orderId || undefined,
        path: this.filters.path || undefined,
        limit: 2000
      });
    },

    async onTabChange(tab) {
      switch (tab.props.name) {
        case 'errors':
          if (this.errors.length === 0) {
            await this.fetchErrors();
          }
          break;
        case 'thirdParty':
          if (this.thirdParty.length === 0) {
            await this.fetchThirdParty();
          }
          break;
        case 'performance':
          await this.fetchPerformance();
          break;
        case 'analytics':
          if (!this.stats) {
            await this.fetchStats();
          }
          break;
      }
    },

    clearFilters() {
      this.filters = {
        search: '',
        type: '',
        level: '',
        path: '',
        statusCode: '',
        minDuration: '',
        accessionId: '',
        orderId: ''
      };
      this.pagination.allLogs.page = 1;
      this.fetchLogs();
    },

    handleAllLogsSizeChange() {
      this.pagination.allLogs.page = 1;
    },

    handleAllLogsPageChange() {
      // Page changed
    },

    showLogDetail(row) {
      this.selectLog(row);
    },

    showResponseDetail(row) {
      this.responseDetailLog = row;
      this.responseTab = row.responseBody ? 'response' : 'request';
      this.jsonSearchRequest = '';
      this.jsonSearchResponse = '';
      this.showResponseDialog = true;
    },

    showErrorSamples(errorGroup) {
      this.samplesDialogTitle = `Error Samples: ${this.truncateMessage(errorGroup.message, 50)}...`;
      this.sampleLogs = errorGroup.samples || [];
      this.pagination.samples.page = 1;
      this.showSamplesDialog = true;
    },

    showServiceCalls(service) {
      this.samplesDialogTitle = `${service.service} Calls`;
      this.sampleLogs = service.calls || [];
      this.pagination.samples.page = 1;
      this.showSamplesDialog = true;
    },

    showSlowSamples(endpoint) {
      this.samplesDialogTitle = `Slow Requests: ${endpoint.endpoint}`;
      this.sampleLogs = endpoint.samples || [];
      this.pagination.samples.page = 1;
      this.showSamplesDialog = true;
    },

    formatTime(timestamp) {
      if (!timestamp) return '--';
      return moment(timestamp).format('YYYY-MM-DD HH:mm:ss');
    },

    formatJson(data) {
      if (!data) return '--';
      try {
        if (typeof data === 'string') {
          return data;
        }
        return JSON.stringify(data, null, 2);
      } catch {
        return String(data);
      }
    },

    truncateMessage(text, maxLen) {
      if (!text) return '';
      if (text.length <= maxLen) return text;
      return text.substring(0, maxLen) + '...';
    },

    highlightSearch(text, search) {
      if (!search || !text) return text;
      const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      return text.replace(regex, '<mark class="highlight">$1</mark>');
    },

    getTypeTagColor(type) {
      const colors = {
        'API': '',
        'ERROR': 'danger',
        'THIRD_PARTY': 'warning',
        'EVENT': 'success',
        'PERFORMANCE': 'info',
        'FAX': 'info',
        'EMAIL': 'info',
        'DATABASE': '',
        'SYSTEM': ''
      };
      return colors[type] || '';
    },

    getLevelTagColor(level) {
      const colors = {
        'DEBUG': 'info',
        'INFO': '',
        'WARN': 'warning',
        'ERROR': 'danger',
        'CRITICAL': 'danger'
      };
      return colors[level] || '';
    },

    getDurationClass(duration) {
      if (duration > 5000) return 'duration-critical';
      if (duration > 3000) return 'duration-slow';
      if (duration > 1000) return 'duration-warning';
      return 'duration-normal';
    },

    getStatusClass(statusCode) {
      if (statusCode >= 500) return 'status-error';
      if (statusCode >= 400) return 'status-warning';
      if (statusCode >= 200 && statusCode < 300) return 'status-success';
      return '';
    },

    getErrorRate(service) {
      if (!service.count) return 0;
      return (service.errors / service.count) * 100;
    },

    getEndpointErrorRate(endpoint) {
      if (!endpoint.count) return 0;
      return (endpoint.errorCount / endpoint.count) * 100;
    },

    handleEndpointSort({ prop, order }) {
      this.endpointSort = { prop, order };
      this.pagination.allEndpoints.page = 1;
    }
  }
};
</script>

<style scoped>
.system-logs-view {
  padding: 20px;
}

.page-title {
  margin: 0;
  font-size: 24px;
  color: #333;
}

.align-items-center {
  align-items: center;
}

.stats-cards {
  margin-bottom: 20px;
}

.stat-card {
  background: #f5f7fa;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}

.stat-card.stat-error {
  background: #fef0f0;
}

.stat-card.stat-third-party {
  background: #fdf6ec;
}

.stat-card.stat-performance {
  background: #f0f9ff;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  color: #333;
}

.stat-label {
  font-size: 12px;
  color: #666;
  margin-top: 5px;
}

.filter-section {
  margin-bottom: 15px;
  padding: 15px;
  background: #fafafa;
  border-radius: 4px;
}

.logs-table,
.errors-summary,
.third-party-summary,
.performance-summary {
  background: #fff;
  border-radius: 4px;
}

.pagination-wrapper {
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
  padding: 10px;
  background: #fafafa;
  border-radius: 4px;
}

.log-time {
  font-family: monospace;
  font-size: 11px;
  color: #666;
}

.log-message {
  word-break: break-word;
  font-size: 12px;
}

.error-message {
  color: #f56c6c;
}

.error-message-text {
  color: #f56c6c;
  word-break: break-word;
}

.service-name {
  font-weight: 500;
}

.endpoint-name {
  font-family: monospace;
  font-size: 13px;
}

.accession-id, .order-id {
  font-family: monospace;
  font-size: 11px;
  color: #409eff;
  font-weight: 500;
}

.accession-id-detail, .order-id-detail {
  font-family: monospace;
  color: #409eff;
  font-weight: 600;
}

.api-path {
  font-family: monospace;
  font-size: 11px;
  color: #606266;
}

.duration-normal {
  color: #67c23a;
}

.duration-warning {
  color: #e6a23c;
}

.duration-slow {
  color: #f56c6c;
}

.duration-critical {
  color: #f56c6c;
  font-weight: bold;
}

.status-success {
  color: #67c23a;
}

.status-warning {
  color: #e6a23c;
}

.status-error {
  color: #f56c6c;
}

.error-rate-high {
  color: #f56c6c;
  font-weight: bold;
}

.analytics-card {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.analytics-card h4 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 16px;
}

.analytics-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.analytics-header h4 {
  margin: 0;
}

.endpoint-path {
  font-family: monospace;
  font-size: 12px;
  color: #606266;
  word-break: break-all;
}

.log-detail, .response-detail {
  max-height: 600px;
  overflow-y: auto;
}

.log-message-detail,
.error-message-detail,
.stack-trace,
.json-body {
  background: #f5f7fa;
  padding: 15px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 250px;
  overflow-y: auto;
}

.error-message-detail {
  background: #fef0f0;
  color: #f56c6c;
}

.stack-trace {
  background: #fdf6ec;
  color: #e6a23c;
}

.search-json {
  margin-bottom: 10px;
}

h4 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #333;
}

:deep(.highlight) {
  background-color: #ffff00;
  padding: 1px 2px;
  border-radius: 2px;
}
</style>
